import { useContext, useEffect, useRef } from 'react';

import { Box } from '@zillow/constellation-10';
import { IconPetsFilled } from '@zillow/constellation-icons';

import { ModularListingContext } from '../../../containers/CreateListingPage/context';
import { WizardContext } from '../../../components/Wizard/context';

import { DISPLAY_SERVER_ERROR_AT } from '../../../constants/WizardConstants';

import actions from '../../../containers/CreateListingPage/actions';

import FormSectionContainer from './components/FormSectionContainer';
import PageWizard from '../../../components/Wizard/PageWizard';
import WizardAlert from '../../../components/WizardAlert';
import WizardTipCardContainer from '../../WizardTipCardContainer';
import WizardTipContent from './components/WizardTipContent';

import { StyledDivCustomLeftSpacing } from '../../../components/StyledComponents/StyledWizardForm';
import { WizardForm } from '../../../styles/wizard';

import type { FormEvent, ReactNode } from 'react';
import type { PageWizardProps } from '../../../components/Wizard/PageWizard';

const DIRECTION = {
  NEXT: 'next',
  BACK: 'back',
};

type Props = {
  onGoToPrevStep: () => void;
  onGoToNextStep: () => void;
  onExitWizard: () => void;
  wizardTitle: ReactNode;
  progressStepper: ReactNode;
  totalSteps: number;
  currentStepIndex: number;
  showDesktopViewBreakpoint: PageWizardProps['showDesktopViewBreakpoint'];
};

const PetPolicyAndPetFee = ({
  onGoToNextStep,
  onGoToPrevStep,
  onExitWizard,
  wizardTitle,
  progressStepper,
  totalSteps,
  currentStepIndex,
  showDesktopViewBreakpoint,
  ...rest
}: Props) => {
  const {
    // @ts-expect-error
    wizardState: { isFetchingData },
    reducerWizardDispatch,
  } = useContext(WizardContext);

  const {
    modularListingState: { hasFieldError, shouldSaveOnBlur, isSaveComplete, serverErrorObj },
    reducerDispatch,
  } = useContext(ModularListingContext);

  const initialRender = useRef(true);
  const directionIntention = useRef<(typeof DIRECTION)[keyof typeof DIRECTION] | null>(null);

  useEffect(() => {
    // do not save on blur
    actions.updateShouldSaveOnBlur({ reducerDispatch, shouldSaveOnBlur: false });

    // save is not complete, but needs to click next
    actions.updateIsSaveComplete({ reducerDispatch, isSaveComplete: false });

    // clear field errors
    actions.updateHasFieldError({ reducerDispatch, hasFieldError: false });
    // keep save disabled in sync with field errors (normally we do not do this)
    actions.updateIsSaveDisabled({ reducerDispatch, isSaveDisabled: false });

    return () => {
      // reset
      actions.updateShouldSaveOnBlur({ reducerDispatch, shouldSaveOnBlur: true });
    };
  }, []);

  useEffect(() => {
    // short circuit on initial render
    if (initialRender.current === true) {
      initialRender.current = false;
      return;
    }

    const { fieldName } = serverErrorObj;

    if (isSaveComplete === false && !fieldName && !isFetchingData) {
      if (directionIntention.current === DIRECTION.NEXT) {
        onGoToNextStep();
      } else if (directionIntention.current === DIRECTION.BACK) {
        onGoToPrevStep();
      }
    }
  }, [isSaveComplete, isFetchingData]);

  const onNextClick = () => {
    directionIntention.current = 'next';

    // communicate that cta has been clicked
    actions.updateIsSaveComplete({ reducerDispatch, isSaveComplete: true });
  };

  const onBackClick = () => {
    directionIntention.current = 'back';

    // communicate that cta has been clicked
    actions.updateIsSaveComplete({ reducerDispatch, isSaveComplete: true });
  };

  return (
    <PageWizard
      {...rest}
      title=""
      wizardTitle={wizardTitle}
      progressStepper={progressStepper}
      totalSteps={totalSteps}
      currentStepIndex={currentStepIndex}
      isNextDisabled={hasFieldError}
      isBackDisabled={hasFieldError}
      onNextClick={onNextClick}
      onBackClick={onBackClick}
      onExitTriggered={onExitWizard}
      showDesktopViewBreakpoint={showDesktopViewBreakpoint}
    >
      <div data-testid="PetPolicyAndPetFee">
        <WizardAlert serverErrorObj={serverErrorObj} displayAt={DISPLAY_SERVER_ERROR_AT.PAGE} />

        <Box
          css={{
            display: 'grid',
            gridTemplateColumns: {
              base: 'repeat(6, 1fr)',
              lg: 'repeat(12, 1fr)',
            },
            gap: '400',
          }}
        >
          <Box css={{ gridColumnEnd: { base: 'span 6', lg: 'span 12', xl: 'span 7' } }}>
            {/* cast as div to prevent cannot appear as a descendant of form error */}
            <WizardForm asChild={true} onSubmit={(e: FormEvent<HTMLFormElement>) => e.preventDefault()}>
              <div>
                <FormSectionContainer />
              </div>
            </WizardForm>
          </Box>

          {/* tip component */}
          <Box css={{ gridColumnEnd: { base: 'span 6', lg: 'span 12', xxl: 'span 5' } }}>
            <StyledDivCustomLeftSpacing>
              <WizardTipCardContainer
                header="Renters love their pets"
                icon={<IconPetsFilled />}
                showDesktopViewBreakpoint={showDesktopViewBreakpoint}
              >
                <WizardTipContent />
              </WizardTipCardContainer>
            </StyledDivCustomLeftSpacing>
          </Box>
        </Box>
      </div>
    </PageWizard>
  );
};

export default PetPolicyAndPetFee;
